/**
 * MOTOR DE VIDEOVIGILANCIA — cobertura, nivel de detalle, PoE, cableado y grabación.
 * =============================================================================
 *
 * Tercer módulo del levantamiento guiado, y el primero donde la pregunta del cliente no
 * es «¿cuántas cámaras?» sino **«¿voy a poder reconocer al que se metió?»**. Son cosas
 * distintas y la diferencia se mide: una cámara puede ver un jardín entero y no servir
 * para identificar a nadie.
 *
 * Por eso este módulo no dibuja «el cono de la cámara» y ya. Dibuja **hasta dónde alcanza
 * cada nivel de detalle**, que es lo que se le promete al cliente y lo que se le puede
 * cobrar.
 *
 * ---------------------------------------------------------------------------
 * LO QUE APRENDIÓ ESTE ARCHIVO AL CONSTRUIRSE, Y VALE LA PENA LEER
 *
 * Se implementó primero el modelo geométrico —resolución entre ancho de escena— y se
 * contrastó contra las distancias que el propio fabricante publica. **No coinciden.** El
 * modelo simple da distancias entre 30% y 57% MÁS CORTAS que la ficha de Hikvision para
 * la misma cámara. Los números están en `DISCREPANCIA_MODELO`.
 *
 * No se ajustó el modelo con un factor para que cuadrara. Se hizo lo mismo que con las
 * bombas: **cuando el fabricante publica el dato, manda el fabricante.** El modelo
 * geométrico se queda como respaldo para cámaras sin DORI publicado, marcado como
 * conservador.
 * ============================================================================= */

import { area, dist, dentro, alcanza, cobertura, arbolMinimo } from './geometria.js'
import { resolver, renglon } from './catalogo-tecnico.js'

export { area, dist, dentro, cobertura }

/* ============================================================================
   1. NIVELES DE DETALLE — la escala DORI
   ========================================================================== */

/**
 * DORI — detección, observación, reconocimiento e identificación.
 *
 * `FUENTE:` norma **IEC 62676-4**, reproducida en el documento técnico de Axis
 * *«Pixel density based on IEC 62676-4:2014»* (`axis.com`, extraído del PDF oficial), y
 * coincidente con la ficha de rendimiento de Hikvision y con la calculadora publicada de
 * `cctvplanner.io`. Tres fuentes independientes con los mismos cuatro valores.
 *
 * La unidad es **píxeles por metro sobre el objetivo**: cuántos píxeles de la imagen caen
 * dentro de un metro real a esa distancia. No es la resolución de la cámara: una cámara
 * de 4 MP identifica a 6 m y apenas detecta a 67 m, con el mismo sensor.
 *
 * **Por qué esto es el corazón del rubro:** el cliente cree que compra «una cámara que
 * vea la entrada». Lo que necesita es *identificar* en la puerta y *detectar* en la
 * barda, y son dos cámaras distintas. Quien no sabe esto vende una sola y queda mal.
 */
export const DORI = [
  {
    id: 'deteccion', nombre: 'Detección', px: 25,
    que: 'Se distingue que hay alguien, y por dónde va.',
    para: 'Perímetro, barda, cochera. Para saber que algo se movió.'
  },
  {
    id: 'observacion', nombre: 'Observación', px: 62.5,
    que: 'Se ve qué trae puesto y qué está haciendo.',
    para: 'Patios y pasillos. Para seguir lo que pasó.'
  },
  {
    id: 'reconocimiento', nombre: 'Reconocimiento', px: 125,
    que: 'Se reconoce a alguien que ya se conoce.',
    para: 'Accesos internos y áreas de servicio.'
  },
  {
    id: 'identificacion', nombre: 'Identificación', px: 250,
    que: 'Se identifica a un desconocido. Es lo que sirve ante un ministerio público.',
    para: 'La puerta principal, la reja, la caja fuerte. Siempre a la altura de la cara.'
  }
]

export const nivelPorId = id => DORI.find(n => n.id === id) || DORI[2]

/**
 * ⚠️ La norma cambió en 2025 y conviene saberlo antes de que un cliente lo mencione.
 *
 * `IEC 62676-4:2025` agrega una escala paralela de siete pasos llamada **OODPCVS**
 * (Overview 20, Outline 40, Discern 80, Perceive 125, Characterise 250, Validate 500,
 * Scrutinise 1500 px/m), que convive con DORI en vez de sustituirla.
 *
 * `sinVerificar:` los siete valores salen de dos resúmenes secundarios —el blog de Axis
 * sobre la revisión y la nota de JVSG—, **no del texto de la norma**, que es de pago. No
 * se cargan como criterio de diseño: se dejan escritos aquí para que quien los necesite
 * sepa que existen y de dónde partir. El cotizador trabaja con DORI.
 */
export const OODPCVS_2025 = {
  sinVerificar: true,
  fuente: 'resúmenes secundarios de la revisión 2025; el texto de la norma es de pago',
  escala: { overview: 20, outline: 40, discern: 80, perceive: 125, characterise: 250, validate: 500, scrutinise: 1500 }
}

/* ============================================================================
   2. CATÁLOGO DE CÁMARAS
   ========================================================================== */

/**
 * CÁMARAS — datos de ficha del fabricante, incluidas sus distancias DORI publicadas.
 *
 * `FUENTE:` ficha técnica oficial de Hikvision del modelo DS-2CD2143G2-I
 * (`assets.hikvision.com`, PDF del 2025-10-21), leída con poppler.
 *
 * Se eligió Hikvision porque es de las marcas con más presencia en México y publica las
 * distancias DORI de cada distancia focal, que es justo el dato caro de conseguir.
 * **Qué marca distribuya Juan es decisión suya**: si stockea otra, se sustituye esta
 * tabla por su ficha y el motor no cambia.
 *
 * `dori` son los metros publicados para cada nivel. `fovH` es el campo de visión
 * horizontal en grados, que es lo que dibuja el sector en el plano.
 */
export const CAMARAS = [
  {
    id: 'hik-2143g2-28', modelo: 'Hikvision DS-2CD2143G2-I', lente: '2.8 mm',
    mp: 4, resH: 2688, resV: 1520,
    fovH: 103, fovV: 55, fovDiag: 122,
    dori: { deteccion: 67, observacion: 26, reconocimiento: 13, identificacion: 6 },
    poe: '802.3af', poeClase: 3, watts: 6.5,
    para: 'Cuartos y patios. Abre mucho, así que ve más superficie pero identifica más cerca.'
  },
  {
    id: 'hik-2143g2-4', modelo: 'Hikvision DS-2CD2143G2-I', lente: '4 mm',
    mp: 4, resH: 2688, resV: 1520,
    fovH: 84, fovV: 45, fovDiag: 100,
    dori: { deteccion: 80, observacion: 31, reconocimiento: 16, identificacion: 8 },
    poe: '802.3af', poeClase: 3, watts: 6.5,
    para: 'Accesos y pasillos. Cierra el ángulo y por eso identifica más lejos.'
  }
]

export const camaraPorId = id => CAMARAS.find(c => c.id === id) || CAMARAS[0]

/* ============================================================================
   3. DENSIDAD DE PÍXELES — el modelo, y por qué no manda
   ========================================================================== */

/**
 * Ancho de escena que abarca una cámara a cierta distancia, en metros.
 *
 * Geometría exacta sobre un plano perpendicular al eje óptico:
 *
 *     W = 2 · d · tan(θ / 2)
 *
 * donde `θ` es el campo de visión horizontal. No es una estimación ni una tabla: es
 * trigonometría.
 */
export const anchoEscena = (fovH, distancia) =>
  2 * distancia * Math.tan((fovH * Math.PI / 180) / 2)

/** Densidad de píxeles sobre el objetivo, en px/m, según el modelo geométrico. */
export const densidadPixeles = (cam, distancia) =>
  distancia > 0 ? cam.resH / anchoEscena(cam.fovH, distancia) : Infinity

/** Distancia a la que el modelo geométrico deja de cumplir un nivel DORI. */
export const distanciaModelo = (cam, nivelId) =>
  cam.resH / (2 * nivelPorId(nivelId).px * Math.tan((cam.fovH * Math.PI / 180) / 2))

/**
 * DISCREPANCIA MEDIDA ENTRE EL MODELO Y LA FICHA — se registra en vez de taparse.
 *
 * Contrastando el modelo geométrico contra las distancias que publica Hikvision para las
 * mismas dos cámaras, con los mismos 2688 px horizontales:
 *
 *   DS-2CD2143G2-I 2.8 mm (FOV 103°)      modelo    ficha    ficha/modelo
 *     Identificación (250 px/m)            4.28 m     6 m        1.40
 *     Reconocimiento (125 px/m)            8.55 m    13 m        1.52
 *     Observación     (62.5 px/m)         17.11 m    26 m        1.52
 *     Detección       (25 px/m)           42.77 m    67 m        1.57
 *
 *   DS-2CD2143G2-I 4 mm (FOV 84°)
 *     Identificación                       5.97 m     8 m        1.34
 *     Reconocimiento                      11.94 m    16 m        1.34
 *     Observación                         23.89 m    31 m        1.30
 *     Detección                           59.72 m    80 m        1.34
 *
 * **No se pudo reproducir la cuenta del fabricante.** Se probó el modelo angular
 * —`W = d · θ` en radianes en vez de `2·d·tan(θ/2)`— y acerca el caso de 2.8 mm pero no
 * el de 4 mm, así que tampoco es eso. Axis documenta que sus propias distancias DORI se
 * calculan *«usando el centro de la imagen como referencia y considerando la distorsión
 * del lente»*, lo que explica la dirección del sesgo pero no permite rehacer el número.
 *
 * **Decisión:** manda la ficha, igual que con las curvas de bomba. El modelo geométrico
 * se conserva para cámaras sin DORI publicado y va marcado como conservador — se queda
 * corto, que es el lado seguro: propone más cámaras, no menos.
 *
 * `sinVerificar` NO aplica aquí: los dos números son verificables y están medidos. Lo que
 * no se conoce es el método del fabricante.
 */
export const DISCREPANCIA_MODELO = {
  medida: '30% a 57% más corto que la ficha',
  porque: 'el fabricante calcula en el centro de la imagen y considera distorsión del lente',
  decision: 'manda la ficha cuando existe; el modelo es el respaldo conservador'
}

/**
 * Alcance de un nivel DORI para una cámara, con su procedencia.
 *
 * Es la función que se usa en todo el módulo, y siempre dice de dónde salió el número.
 */
export function alcanceDe (cam, nivelId) {
  const publicado = cam.dori && cam.dori[nivelId]
  if (publicado) {
    return {
      metros: publicado, de: 'ficha',
      porque: `Distancia publicada por el fabricante para ${cam.modelo} con lente de ${cam.lente}.`
    }
  }
  const m = distanciaModelo(cam, nivelId)
  return {
    metros: Math.round(m * 10) / 10, de: 'modelo',
    porque: 'Esta cámara no publica DORI, así que sale del modelo geométrico. ' +
            'Se queda corto respecto a lo que suelen publicar los fabricantes: propone más cámaras, no menos.'
  }
}

/* ============================================================================
   4. COBERTURA SOBRE EL PLANO
   ========================================================================== */

/**
 * Qué parte del recinto queda cubierta a un nivel de detalle dado.
 *
 * Reusa `cobertura` de `geometria.js` —la misma que el aspersor— traduciendo cada cámara
 * a un sector: centro, radio igual al alcance de ese nivel, y arco igual a su campo de
 * visión, orientado hacia donde apunta.
 *
 * Que el aspersor y la cámara compartan la misma función no es una casualidad feliz: un
 * chorro de agua y un cono de visión son el mismo problema de geometría, y tenerlo en un
 * solo lugar es lo que hace que agregar rubros nuevos sea barato.
 */
/**
 * Traduce una cámara colocada en el plano al sector que entiende `geometria.js`.
 *
 * ⚠️ Cuidado con el ángulo, que es donde se equivoca uno solo. En `geometria.js`,
 * `orientacion` es **donde EMPIEZA el arco**, y el arco se abre desde ahí en sentido
 * antihorario. Una cámara, en cambio, se apunta por su **centro**: «mira al norte» quiere
 * decir que el eje óptico va al norte y el campo se reparte a los dos lados.
 *
 * Por eso hay que restar medio campo de visión. Sin esta resta la cobertura sale girada
 * media apertura —51.5° en el lente de 2.8 mm—, y el plano mostraría cubierto justo lo
 * que no lo está.
 */
export function sectorDe (c, nivelId) {
  const cam = camaraPorId(c.modelo)
  const arco = cam.fovH
  return {
    x: c.x, y: c.y,
    radio: alcanceDe(cam, nivelId).metros,
    arco,
    /** `rumbo` es hacia dónde APUNTA la cámara; el arco empieza media apertura antes. */
    orientacion: ((c.rumbo || 0) - arco / 2 + 360) % 360
  }
}

export function coberturaNivel (recinto, camaras, nivelId, paso = 0.5) {
  return cobertura(recinto, camaras.map(c => sectorDe(c, nivelId)), paso)
}

/** ¿Este punto queda cubierto a este nivel por alguna cámara? */
export function puntoCubierto (punto, camaras, nivelId) {
  // `alcanza` recibe (elemento, punto) — en ese orden, igual que para el aspersor.
  return camaras.some(c => alcanza(sectorDe(c, nivelId), punto))
}

/* ============================================================================
   5. ALIMENTACIÓN Y CABLEADO
   ========================================================================== */

/**
 * Presupuesto de PoE, por norma IEEE 802.3.
 *
 * `FUENTE:` los valores de 802.3af y 802.3at son los que publica la propia ficha de
 * Hikvision al declarar «PoE: 802.3af, Class 3, 36 V to 57 V, máx. 6.5 W», y coinciden
 * con la definición de clases del estándar.
 *
 * `pse` es lo que entrega el switch por puerto; `pd` es lo que puede consumir el equipo.
 * La diferencia es la pérdida máxima admitida en el cable, y por eso el presupuesto se
 * hace con `pse`, no con la suma de consumos.
 */
export const POE = {
  '802.3af': { pse: 15.4, pd: 12.95, nombre: 'PoE' },
  '802.3at': { pse: 30.0, pd: 25.5, nombre: 'PoE+' },
  '802.3bt': { pse: 60.0, pd: 51.0, nombre: 'PoE++' }
}

/**
 * Longitud máxima de un enlace de cobre: **100 m de canal**, de los cuales 90 son de
 * cableado horizontal fijo y 10 se reservan para los latiguillos de cada extremo.
 *
 * `FUENTE:` límite de canal de TIA-568 / ISO 11801, el mismo que respeta todo equipo
 * Ethernet sobre par trenzado.
 *
 * Es el error clásico del rubro: la cámara del fondo del terreno queda a 120 m del rack y
 * nadie lo nota hasta que el enlace no levanta. Aquí se marca en el plano.
 */
export const LIMITE_UTP = { canal: 100, horizontal: 90, latiguillos: 10 }

/**
 * Cableado: un tramo por cámara desde el rack, por trayectoria ortogonal.
 *
 * A diferencia del riego —donde la tubería se ramifica y conviene un árbol mínimo— **cada
 * cámara necesita su propio cable hasta el switch**. No se pueden compartir tramos: son
 * enlaces punto a punto. Por eso aquí NO se usa `arbolMinimo`, aunque esté disponible.
 */
export function cableado (rack, camaras, holguraObra = 1.10) {
  const tramos = camaras.map((c, i) => {
    const recto = Math.abs(c.x - rack.x) + Math.abs(c.y - rack.y)
    const conHolgura = Math.ceil(recto * holguraObra)
    return {
      camara: i, metros: conHolgura, directo: recto,
      excedeLimite: conHolgura > LIMITE_UTP.canal,
      porque: conHolgura > LIMITE_UTP.canal
        ? `Son ${conHolgura} m y el límite del cobre es ${LIMITE_UTP.canal}. Hay que meter un ` +
          'switch intermedio o pasar a fibra: alargar el cable no es opción.'
        : null
    }
  })
  return {
    tramos,
    metros: tramos.reduce((s, t) => s + t.metros, 0),
    fueraDeLimite: tramos.filter(t => t.excedeLimite).length
  }
}

/** Presupuesto de energía del switch, con la reserva que evita comprarlo dos veces. */
export function presupuestoPoe (camaras, reserva = 0.20) {
  const detalle = camaras.map(c => {
    const cam = camaraPorId(c.modelo)
    return { modelo: cam.modelo, watts: cam.watts, norma: cam.poe }
  })
  const consumo = detalle.reduce((s, d) => s + d.watts, 0)
  const conReserva = consumo * (1 + reserva)
  return {
    consumo, conReserva, reserva,
    porque: `Las ${camaras.length} cámaras consumen ${consumo.toFixed(1)} W. Se pide un switch de ` +
            `al menos ${Math.ceil(conReserva)} W de presupuesto PoE, con ${Math.round(reserva * 100)}% de ` +
            'reserva para no tener que cambiarlo al agregar una cámara.',
    puertos: camaras.length,
    puertosConReserva: Math.max(4, Math.ceil(camaras.length * 1.25))
  }
}

/* ============================================================================
   6. GRABACIÓN
   ========================================================================== */

/**
 * Almacenamiento necesario, en terabytes.
 *
 *     TB = Mbps × cámaras × 86400 s × días / 8 / 1e6
 *
 * Aritmética exacta. Lo que NO es exacto es el `mbps`: depende de la escena, porque el
 * códec comprime mucho más un pasillo vacío que un jardín con árboles moviéndose.
 *
 * La ficha del DS-2CD2143G2-I declara un rango de **32 Kbps a 8 Mbps**, que no sirve para
 * dimensionar. Por eso `MBPS_TIPICO` va marcado: es un valor de trabajo, no de ficha, y
 * se calibra midiendo la primera instalación real durante una semana.
 */
export const MBPS_TIPICO = {
  sinVerificar: true,
  interior: 2.5,
  exterior: 4.0,
  porque: 'La ficha publica un rango de 32 Kbps a 8 Mbps según escena y códec. Estos son ' +
          'valores de trabajo para dimensionar; se calibran midiendo una instalación real.'
}

export function almacenamiento ({ camaras, dias = 15, mbps = MBPS_TIPICO.interior }) {
  const tb = (mbps * camaras.length * 86400 * dias) / 8 / 1e6
  return {
    tb: Math.round(tb * 100) / 100,
    /* Los discos se venden en pasos. Se sube al inmediato superior, nunca se redondea
       hacia abajo: quedarse corto significa perder los días que el cliente contrató. */
    tbComercial: [1, 2, 4, 6, 8, 10, 12, 16, 18, 20].find(d => d >= tb) || 20,
    dias, mbps,
    porque: `${camaras.length} cámaras a ${mbps} Mbps durante ${dias} días continuos.`
  }
}

/* ============================================================================
   7. LA CADENA COMPLETA
   ========================================================================== */

/**
 * Del plano al material. Es lo que se ejecuta al tocar «calcular».
 *
 * `nivelObjetivo` es lo que el cliente pidió: si dijo «quiero poder identificar quién
 * toca el timbre», el objetivo es identificación y la cobertura se mide contra eso.
 */
export function calcular ({
  recinto, camaras, rack, nivelObjetivo = 'reconocimiento', dias = 15,
  mbps = MBPS_TIPICO.interior, insumos = [], holguraObra = 1.10
}) {
  const cobertura = coberturaNivel(recinto, camaras, nivelObjetivo)
  const red = cableado(rack, camaras, holguraObra)
  const poe = presupuestoPoe(camaras)
  const disco = almacenamiento({ camaras, dias, mbps })

  /* La cobertura de CADA nivel, no sólo la del objetivo. Es lo que permite decirle al
     cliente «tu jardín queda detectado al 100% pero identificado sólo en la puerta», que
     es exactamente la conversación que evita un reclamo después. */
  const porNivel = DORI.map(n => ({
    ...n,
    porcentaje: coberturaNivel(recinto, camaras, n.id).porcentaje
  }))

  return {
    areaRecinto: area(recinto),
    nivelObjetivo,
    cobertura,
    porNivel,
    red, poe, disco,
    camaras: camaras.map(c => {
      const cam = camaraPorId(c.modelo)
      return {
        ...c, ficha: cam,
        alcances: Object.fromEntries(DORI.map(n => [n.id, alcanceDe(cam, n.id)]))
      }
    }),
    materiales: materiales({ camaras, red, poe, disco, insumos })
  }
}

/** Lista de material, cobrada con el catálogo de Juan. Mismo patrón que riego y eléctrico. */
export function materiales ({ camaras, red, poe, disco, insumos = [] }) {
  const lista = []

  const porModelo = {}
  for (const c of camaras) porModelo[c.modelo] = (porModelo[c.modelo] || 0) + 1
  for (const [modelo, cantidad] of Object.entries(porModelo)) {
    const cam = camaraPorId(modelo)
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'camara', camara: modelo }),
      cantidad, unidad: 'pieza',
      de: `${cantidad} en el plano. ${cam.modelo} de ${cam.lente}, que identifica hasta ` +
          `${cam.dori.identificacion} m`
    }))
  }

  lista.push(renglon({
    insumo: resolver(insumos, { clase: 'utp' }),
    cantidad: red.metros, unidad: 'm',
    de: `Un tramo por cámara desde el rack, por trayectoria ortogonal, con holgura de obra`
  }))

  lista.push(renglon({
    insumo: resolver(insumos, { clase: 'switch-poe', puertos: puertosComerciales(poe.puertosConReserva) }),
    cantidad: 1, unidad: 'pieza',
    de: poe.porque
  }))

  lista.push(renglon({
    insumo: resolver(insumos, { clase: 'nvr', canales: puertosComerciales(camaras.length) }),
    cantidad: 1, unidad: 'pieza',
    de: `Un canal por cámara, al paso comercial`
  }))

  lista.push(renglon({
    insumo: resolver(insumos, { clase: 'disco', tb: disco.tbComercial }),
    cantidad: 1, unidad: 'pieza',
    de: disco.porque
  }))

  return lista
}

/** Los switches y grabadores se venden en 4, 8, 16, 24 y 32. No existe uno de 6. */
export const puertosComerciales = n => [4, 8, 16, 24, 32].find(p => p >= n) || 32
